import { handleRTKQueryError } from 'services/Error';
import { mapConversation, mapMessage } from 'services/userSupportHelpers';
import { TagTypes, userSupportApi } from 'store/api/userSupportApi';
import {
    syncChangedConversationToSlice,
    syncSentMessage,
} from 'store/userSupport/userSupportRestSync';

/**
 * Seconds a cached unread-count result is reused by a newly mounting component. The header and
 * a referral's messages both load counts on mount, usually within moments of each other.
 */
export const UNREAD_COUNTS_STALE_SECONDS = 30;

/**
 * Fetching a conversation's messages marks it read. Once that lands, the cached conversation
 * lists drop its unread marker (refetching a list for one known field would cost a request) and
 * unread counts refetch; requesting them alongside the read can return the pre-read counts.
 */
export const syncConversationRead = async (
    conversationId,
    { dispatch, getState, queryFulfilled },
    apiUtil,
) => {
    try {
        await queryFulfilled;
    } catch {
        return;
    }
    const markRead = (conversations) => {
        const conversation = conversations.find((c) => c.conversationId === conversationId);
        if (conversation) {
            conversation.isUnread = false;
            conversation.subject.isUnread = false;
        }
    };
    ['getReferralConversations', 'searchReferralConversations'].forEach((endpointName) =>
        apiUtil
            .selectCachedArgsForQuery(getState(), endpointName)
            .forEach((args) => dispatch(apiUtil.updateQueryData(endpointName, args, markRead))),
    );
    dispatch(apiUtil.invalidateTags([TagTypes.UNREAD_COUNTS]));
};

/**
 * Referral chat pane endpoints: REST writes (userSupportRestWrites) and on-demand reads
 * (userSupportRestFetch) that replace the WebSocket.
 */
export const userSupportChatApi = userSupportApi.injectEndpoints({
    endpoints: (build) => ({
        getConversation: build.query({
            query: (conversationId) => ({
                url: 'admin/conversations',
                params: { conversation_id: conversationId },
            }),
            providesTags: (_results, _error, conversationId) => [
                { type: TagTypes.CONVERSATION, id: conversationId },
            ],
            transformResponse: mapConversation,
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving the conversation.',
            ),
        }),
        getConversationByDisplayId: build.query({
            query: (displayId) => ({
                url: 'admin/conversations',
                params: { display_id: displayId },
            }),
            transformResponse: mapConversation,
            transformErrorResponse: handleRTKQueryError('Unable to find conversation.'),
        }),
        searchReferralConversations: build.query({
            query: ({ referralId, searchTerm }) => ({
                url: 'admin/conversations/list',
                method: 'POST',
                params: { referral_id: referralId, search_term: searchTerm },
            }),
            providesTags: (results) =>
                (results ?? []).map(({ conversationId }) => ({
                    type: TagTypes.CONVERSATION,
                    id: conversationId,
                })),
            transformResponse: (response) => response.map(mapConversation),
            transformErrorResponse: handleRTKQueryError('Unable to search conversations.'),
        }),
        // Facts stay in their snake_case API shape; the fact form helpers read them as-is.
        getConversationFacts: build.query({
            query: (conversationId) => `admin/conversations/${conversationId}/facts`,
            providesTags: (_results, _error, conversationId) => [
                { type: TagTypes.CONVERSATION_FACTS, id: conversationId },
            ],
            transformErrorResponse: handleRTKQueryError(
                'An error occurred while retrieving conversation facts.',
            ),
        }),
        getConversationTopics: build.query({
            query: () => 'admin/conversations/topics',
            transformResponse: (response) =>
                response.map((topic) => ({
                    name: topic.title,
                    value: topic.topic_id,
                    secondaryData: topic.short_title,
                })),
            transformErrorResponse: handleRTKQueryError('Unable to fetch conversation topics.'),
        }),
        getTopicFacts: build.query({
            query: (topicId) => `admin/conversations/topics/${topicId}/facts`,
            transformErrorResponse: handleRTKQueryError('Unable to retrieve topic questions.'),
        }),
        // Callers surface write failures with their own toast, so no transformErrorResponse here.
        createConversation: build.mutation({
            query: ({ referralId, body }) => ({
                url: `admin/conversations/referral/${referralId}`,
                method: 'POST',
                body,
            }),
            transformResponse: mapConversation,
            // No UNREAD_COUNTS: the caller selects the new conversation, and that messages read
            // refreshes the counts (syncConversationRead)
            invalidatesTags: (_results, _error, { referralId }) => [
                { type: TagTypes.REFERRAL_CONVERSATIONS, id: referralId },
                TagTypes.PAGED_CONVERSATIONS,
            ],
        }),
        updateConversationFacts: build.mutation({
            query: ({ conversationId, body }) => ({
                url: `admin/conversations/${conversationId}/facts`,
                method: 'PUT',
                body,
            }),
            // A topic change also changes the conversation's subject in the lists
            invalidatesTags: (_results, _error, { conversationId }) => [
                { type: TagTypes.CONVERSATION, id: conversationId },
                { type: TagTypes.CONVERSATION_FACTS, id: conversationId },
            ],
            onQueryStarted: (arg, lifecycle) =>
                syncChangedConversationToSlice(
                    arg,
                    lifecycle,
                    userSupportApi.endpoints.getConversation,
                ),
        }),
        // Callers surface write failures with their own toast, so no transformErrorResponse here.
        sendMessage: build.mutation({
            query: ({ conversationId, details }) => ({
                url: `admin/messages/${conversationId}`,
                method: 'POST',
                body: details,
            }),
            transformResponse: mapMessage,
            invalidatesTags: (_results, _error, { conversationId }) => [
                { type: TagTypes.CONVERSATION, id: conversationId },
                TagTypes.UNREAD_COUNTS,
            ],
            onQueryStarted: (arg, lifecycle) =>
                syncSentMessage(arg, lifecycle, userSupportApi.util),
        }),
        updateConversationStatus: build.mutation({
            query: ({ conversationId, newStatus }) => ({
                url: `admin/conversations/${conversationId}/status`,
                method: 'PUT',
                params: { new_status: newStatus },
            }),
            // A status change also posts a system message to the conversation. The messages
            // refetch refreshes unread counts once it lands (syncConversationRead).
            invalidatesTags: (_results, _error, { conversationId }) => [
                { type: TagTypes.CONVERSATION, id: conversationId },
                { type: TagTypes.MESSAGES, id: conversationId },
            ],
            onQueryStarted: (arg, lifecycle) =>
                syncChangedConversationToSlice(
                    arg,
                    lifecycle,
                    userSupportApi.endpoints.getConversation,
                ),
        }),
        updateConversationAssignee: build.mutation({
            query: ({ conversationId, assigneeUpn }) => ({
                url: `admin/conversations/${conversationId}/assignee`,
                method: 'PUT',
                params: assigneeUpn ? { assignee_upn: assigneeUpn } : {},
            }),
            invalidatesTags: (_results, _error, { conversationId }) => [
                { type: TagTypes.CONVERSATION, id: conversationId },
            ],
            onQueryStarted: (arg, lifecycle) =>
                syncChangedConversationToSlice(
                    arg,
                    lifecycle,
                    userSupportApi.endpoints.getConversation,
                ),
        }),
        // Reading a conversation's messages marks it read server-side, so this must hit the
        // network on every select (the api-wide refetchOnMountOrArgChange: true), never the cache.
        getMessages: build.query({
            query: (conversationId) => `admin/messages/${conversationId}`,
            providesTags: (_results, _error, conversationId) => [
                { type: TagTypes.MESSAGES, id: conversationId },
            ],
            transformResponse: (response) => response.map(mapMessage),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred while retrieving messages.',
            ),
            onQueryStarted: (conversationId, lifecycle) =>
                syncConversationRead(conversationId, lifecycle, userSupportApi.util),
        }),
        getReferralConversations: build.query({
            query: (referralId) => ({
                url: 'admin/conversations/list',
                method: 'POST',
                params: { referral_id: referralId },
            }),
            providesTags: (results, _error, referralId) => [
                { type: TagTypes.REFERRAL_CONVERSATIONS, id: referralId },
                ...(results ?? []).map(({ conversationId }) => ({
                    type: TagTypes.CONVERSATION,
                    id: conversationId,
                })),
            ],
            transformResponse: (response) => response.map(mapConversation),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred while retrieving conversations.',
            ),
        }),
        getUnreadCounts: build.query({
            query: () => 'admin/conversations/unread_counts',
            providesTags: [TagTypes.UNREAD_COUNTS],
            // Same shape the socket-fed slice exposes, so badge consumers read either source
            transformResponse: ({ total_unread: totalUnread, by_referral: byReferral }) => ({
                unreadConversationCount: totalUnread,
                referralConversationCounts: Object.fromEntries(
                    Object.entries(byReferral ?? {}).map(([referralId, { open, unread }]) => [
                        referralId,
                        { open, unread },
                    ]),
                ),
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred while retrieving unread counts.',
            ),
        }),
    }),
});

export const {
    useCreateConversationMutation,
    useGetConversationByDisplayIdQuery,
    useGetConversationFactsQuery,
    useGetConversationTopicsQuery,
    useGetMessagesQuery,
    useGetReferralConversationsQuery,
    useGetTopicFactsQuery,
    useGetUnreadCountsQuery,
    useLazyGetTopicFactsQuery,
    useSearchReferralConversationsQuery,
    useSendMessageMutation,
    useUpdateConversationAssigneeMutation,
    useUpdateConversationFactsMutation,
    useUpdateConversationStatusMutation,
} = userSupportChatApi;
